import React from 'react';
import { 
  CurrencyDollarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  BeakerIcon,
  SparklesIcon,
  PlusIcon,
  PencilIcon,
  Square3Stack3DIcon,
  BuildingOfficeIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import { RocketLaunchIcon } from '@heroicons/react/24/solid';

export default function Subscriptions() {
  return (
    <div className="px-8 py-8 max-w-[1400px] mx-auto flex flex-col gap-8 pb-20">
      
      {/* Top Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
        
        {/* Total MRR */}
        <div className="bg-white border border-slate-200 rounded-[24px] p-6 shadow-sm relative overflow-hidden flex flex-col justify-center min-h-[140px]">
          <CurrencyDollarIcon className="absolute -right-4 -top-6 w-32 h-32 text-slate-50 opacity-[0.4]" strokeWidth={2} />
          <div className="relative z-10">
            <p className="text-[11px] font-extrabold text-slate-400 uppercase tracking-widest mb-1">Total MRR</p>
            <div className="flex items-baseline gap-3 mb-1">
              <h2 className="text-[32px] font-extrabold text-slate-900 tracking-tight leading-none">$248,392</h2>
              <span className="text-[12px] font-bold text-emerald-500 bg-emerald-50 px-2 py-0.5 rounded-md">+12.4%</span>
            </div>
            <p className="text-[12px] text-slate-400 font-medium">From previous month</p>
          </div>
        </div>

        {/* Churn Rate */}
        <div className="bg-white border border-slate-200 rounded-[24px] p-6 shadow-sm relative overflow-hidden flex flex-col justify-center min-h-[140px]">
          <ArrowTrendingDownIcon className="absolute -right-4 -top-2 w-28 h-28 text-slate-50 opacity-[0.8]" strokeWidth={2} />
          <div className="relative z-10">
            <p className="text-[11px] font-extrabold text-slate-400 uppercase tracking-widest mb-1">Churn Rate</p>
            <div className="flex items-baseline gap-3 mb-1">
              <h2 className="text-[32px] font-extrabold text-slate-900 tracking-tight leading-none">1.24%</h2>
              <span className="text-[12px] font-bold text-red-500 bg-red-50 px-2 py-0.5 rounded-md">-0.2%</span>
            </div>
            <p className="text-[12px] text-slate-400 font-medium">Healthy threshold (&lt;2%)</p>
          </div>
        </div>

        {/* Active Trials */}
        <div className="bg-white border border-slate-200 rounded-[24px] p-6 shadow-sm relative overflow-hidden flex flex-col justify-center min-h-[140px]">
          <BeakerIcon className="absolute -right-4 -top-2 w-28 h-28 text-slate-50 opacity-[0.8]" strokeWidth={2} />
          <div className="relative z-10">
            <p className="text-[11px] font-extrabold text-slate-400 uppercase tracking-widest mb-1">Active Trials</p>
            <div className="flex items-baseline gap-3 mb-1">
              <h2 className="text-[32px] font-extrabold text-slate-900 tracking-tight leading-none">1,482</h2>
              <span className="text-[11px] font-bold text-brand-600 bg-brand-50 px-2.5 py-0.5 rounded-md border border-brand-100">New</span>
            </div>
            <p className="text-[12px] text-slate-400 font-medium">42% projected conversion</p>
          </div>
        </div>

        {/* New Subs (Today) */}
        <div className="bg-white border border-slate-200 rounded-[24px] p-6 shadow-sm relative overflow-hidden flex flex-col justify-center min-h-[140px]">
          <SparklesIcon className="absolute -right-4 -top-2 w-28 h-28 text-slate-50 opacity-[0.8]" strokeWidth={2} />
          <div className="relative z-10">
            <p className="text-[11px] font-extrabold text-slate-400 uppercase tracking-widest mb-1">New Subs (Today)</p>
            <div className="flex items-baseline gap-3 mb-1">
              <h2 className="text-[32px] font-extrabold text-slate-900 tracking-tight leading-none">84</h2>
              <span className="text-[12px] font-bold text-emerald-500 bg-emerald-50 px-2 py-0.5 rounded-md">+18</span>
            </div>
            <p className="text-[12px] text-slate-400 font-medium">Last update: 2 mins ago</p>
          </div>
        </div>

      </div>

      {/* Main Two-Column Layout */}
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-8">
        
        {/* Left Column: Active Subscription Plans */}
        <div className="xl:col-span-7 flex flex-col gap-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-2">
            <div>
              <h2 className="text-[20px] font-extrabold text-slate-900 tracking-tight">Active Subscription Plans</h2>
              <p className="text-[13px] text-slate-500 font-medium mt-0.5">Manage tier pricing, features, and quotas.</p>
            </div>
            <button className="flex items-center gap-2 bg-brand-600 text-white px-5 py-2.5 rounded-xl text-sm font-bold hover:bg-brand-700 transition-colors shadow-sm shadow-brand-500/20 shrink-0">
              <PlusIcon className="w-5 h-5" /> Create New Plan
            </button>
          </div>

          <div className="flex flex-col gap-4">
            
            {/* Starter Plan */}
            <div className="bg-white border border-slate-200 rounded-[20px] p-6 flex items-center justify-between shadow-sm hover:border-slate-300 transition-colors">
              <div className="flex items-center gap-5">
                <div className="w-14 h-14 bg-slate-100 text-slate-500 rounded-xl flex items-center justify-center shrink-0">
                  <Square3Stack3DIcon className="w-7 h-7" strokeWidth={1.5} />
                </div>
                <div>
                  <h3 className="text-[16px] font-bold text-slate-900 mb-1">Starter (Free)</h3>
                  <p className="text-[12px] text-slate-500 font-medium leading-snug">Basic AI generation, 5 seats, 1GB<br/>storage</p>
                </div>
              </div>
              <div className="flex items-center gap-8">
                <div className="text-right">
                  <p className="text-[15px] font-extrabold text-slate-900">$0.00/mo</p>
                  <p className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest mt-1">12,402 ACTIVE<br/>USERS</p>
                </div>
                <button className="flex items-center gap-2 bg-slate-100 text-slate-600 px-4 py-2.5 rounded-xl text-[13px] font-bold hover:bg-slate-200 transition-colors">
                  <PencilIcon className="w-4 h-4" /> Edit<br/>Plan
                </button>
              </div>
            </div>

            {/* Professional Plan (Highlighted) */}
            <div className="bg-white border-2 border-brand-200 rounded-[20px] p-6 flex items-center justify-between shadow-md shadow-brand-500/10 relative overflow-hidden">
              <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-brand-500"></div>
              <div className="flex items-center gap-5 pl-2">
                <div className="w-14 h-14 bg-brand-50 border border-brand-100 text-brand-600 rounded-xl flex items-center justify-center shrink-0">
                  <RocketLaunchIcon className="w-7 h-7" />
                </div>
                <div>
                  <div className="flex items-center gap-3 mb-1">
                    <h3 className="text-[16px] font-bold text-slate-900">Professional</h3>
                    <span className="px-2.5 py-0.5 bg-brand-50 text-brand-600 text-[9px] font-extrabold uppercase tracking-widest rounded-md border border-brand-100">Most Popular</span>
                  </div>
                  <p className="text-[12px] text-slate-500 font-medium leading-snug">Unlimited AI, 50 seats, 500GB storage,<br/>API Access</p>
                </div>
              </div>
              <div className="flex items-center gap-8">
                <div className="text-right">
                  <p className="text-[15px] font-extrabold text-slate-900">$49.00/mo</p>
                  <p className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest mt-1">4,821 ACTIVE<br/>USERS</p>
                </div>
                <button className="flex items-center gap-2 bg-slate-100 text-slate-600 px-4 py-2.5 rounded-xl text-[13px] font-bold hover:bg-slate-200 transition-colors">
                  <PencilIcon className="w-4 h-4" /> Edit<br/>Plan
                </button>
              </div>
            </div>

            {/* Enterprise Plan */}
            <div className="bg-white border border-slate-200 rounded-[20px] p-6 flex items-center justify-between shadow-sm hover:border-slate-300 transition-colors">
              <div className="flex items-center gap-5">
                <div className="w-14 h-14 bg-slate-100 text-slate-500 rounded-xl flex items-center justify-center shrink-0">
                  <BuildingOfficeIcon className="w-7 h-7" strokeWidth={1.5} />
                </div>
                <div>
                  <h3 className="text-[16px] font-bold text-slate-900 mb-1">Enterprise</h3>
                  <p className="text-[12px] text-slate-500 font-medium leading-snug">Dedicated Support, custom SLAs, On-<br/>prem deployment</p>
                </div>
              </div>
              <div className="flex items-center gap-8">
                <div className="text-right">
                  <p className="text-[15px] font-extrabold text-slate-900">Custom<br/>Pricing</p>
                  <p className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest mt-1">184 ACTIVE<br/>ACCOUNTS</p>
                </div>
                <button className="flex items-center gap-2 bg-slate-100 text-slate-600 px-4 py-2.5 rounded-xl text-[13px] font-bold hover:bg-slate-200 transition-colors">
                  <PencilIcon className="w-4 h-4" /> Edit<br/>Plan
                </button>
              </div>
            </div>

          </div>
        </div>

        {/* Right Column: Audit Logs */}
        <div className="xl:col-span-5 bg-slate-100/60 border border-slate-200 rounded-[24px] p-8 flex flex-col">
          <div className="flex items-center justify-between mb-8">
            <h3 className="text-[13px] font-extrabold text-slate-900 uppercase tracking-widest">Recent Plan Changes</h3>
            <ClockIcon className="w-6 h-6 text-slate-400" />
          </div>

          {/* Timeline Container */}
          <div className="relative pl-3 flex-1 mb-8">
            {/* Vertical Line */}
            <div className="absolute top-2 bottom-4 left-4 w-[2px] bg-slate-200/80 rounded-full"></div>

            <div className="flex flex-col gap-6">
              
              {/* Item 1 */}
              <div className="relative pl-8">
                <div className="absolute left-[-5px] top-1.5 w-3 h-3 bg-emerald-500 rounded-full border-2 border-white shadow-sm ring-4 ring-slate-100/60 z-10"></div>
                <p className="text-[13px] text-slate-600 leading-snug mb-1">
                  <strong className="text-slate-900 font-bold">AlphaCorp.eth</strong> upgraded to <span className="font-bold text-brand-600">Enterprise</span>
                </p>
                <p className="text-[11px] text-slate-400 font-medium">2 minutes ago • ID: #883921</p>
              </div>

              {/* Item 2 */}
              <div className="relative pl-8">
                <div className="absolute left-[-5px] top-1.5 w-3 h-3 bg-brand-500 rounded-full border-2 border-white shadow-sm ring-4 ring-slate-100/60 z-10"></div>
                <p className="text-[13px] text-slate-600 leading-snug mb-1">
                  <strong className="text-slate-900 font-bold">Sarah Jenkins</strong> switched to <strong className="text-slate-900 font-bold">Annual Billing</strong>
                </p>
                <p className="text-[11px] text-slate-400 font-medium">14 minutes ago • Plan: Pro</p>
              </div>

              {/* Item 3 */}
              <div className="relative pl-8">
                <div className="absolute left-[-5px] top-1.5 w-3 h-3 bg-red-400 rounded-full border-2 border-white shadow-sm ring-4 ring-slate-100/60 z-10"></div>
                <p className="text-[13px] text-slate-600 leading-snug mb-1">
                  <strong className="text-slate-900 font-bold">DesignFlow Studio</strong> cancelled subscription
                </p>
                <p className="text-[11px] text-slate-400 font-medium">42 minutes ago • Reason: Price sensitivity</p>
              </div>

              {/* Item 4 */}
              <div className="relative pl-8">
                <div className="absolute left-[-5px] top-1.5 w-3 h-3 bg-blue-300 rounded-full border-2 border-white shadow-sm ring-4 ring-slate-100/60 z-10"></div>
                <p className="text-[13px] text-slate-600 leading-snug mb-1">
                  <strong className="text-slate-900 font-bold">Marcus Web</strong> trial converted to <strong className="text-slate-900 font-bold">Pro</strong>
                </p>
                <p className="text-[11px] text-slate-400 font-medium">1 hour ago • Auto-bill enabled</p>
              </div>

              {/* Item 5 */}
              <div className="relative pl-8">
                <div className="absolute left-[-5px] top-1.5 w-3 h-3 bg-slate-300 rounded-full border-2 border-white shadow-sm ring-4 ring-slate-100/60 z-10"></div>
                <p className="text-[13px] text-slate-600 leading-snug mb-1">
                  <strong className="text-slate-900 font-bold">Skyline Arch</strong> updated payment method
                </p>
                <p className="text-[11px] text-slate-400 font-medium">3 hours ago • Visa **** 4242</p>
              </div>

            </div>
          </div>

          <button className="w-full bg-slate-200/60 hover:bg-slate-200 text-slate-600 py-3 rounded-xl text-[12px] font-extrabold uppercase tracking-widest transition-colors mt-auto">
            View Audit Full Log
          </button>
        </div>

      </div>

      {/* AI Insight Bottom Banner */}
      <div className="bg-[#f0f6ff] border border-brand-100/50 rounded-[32px] p-10 relative overflow-hidden flex flex-col md:flex-row items-center justify-between gap-10 shadow-sm mt-4">
        <div className="flex-1 z-10">
          <span className="inline-block px-3 py-1 bg-white text-brand-600 text-[10px] font-extrabold uppercase tracking-widest rounded-full shadow-sm mb-5">
            AI Insight
          </span>
          <h2 className="text-[28px] font-extrabold text-brand-900 tracking-tight leading-tight mb-4">
            Retention is projected to increase by 8% next month.
          </h2>
          <p className="text-[15px] text-brand-700/80 font-medium leading-relaxed max-w-2xl mb-8">
            Our predictive model indicates that the recent Pro plan feature additions have reduced churning probability for users in the 3-6 month bracket. Consider a targeted "Year-one" loyalty discount for this cohort.
          </p>
          <div className="flex items-center gap-4">
            <button className="bg-[#002666] text-white px-6 py-3 rounded-xl text-[14px] font-bold hover:bg-[#001742] transition-colors shadow-md">
              Analyze Cohorts
            </button>
            <button className="bg-white text-brand-900 px-6 py-3 rounded-xl text-[14px] font-bold hover:bg-brand-50 transition-colors shadow-sm">
              Dismiss
            </button>
          </div>
        </div>

        {/* Right Side Graph Card inside Insight */}
        <div className="bg-white rounded-2xl shadow-xl shadow-brand-500/10 p-6 w-[280px] h-[180px] shrink-0 relative z-10 flex flex-col items-center justify-center border border-white">
          <div className="absolute inset-0 flex items-center justify-center pb-2">
             <svg className="w-full h-24 overflow-visible" viewBox="0 0 100 30" preserveAspectRatio="none">
                <path d="M -10 25 Q 25 25, 50 5 T 110 25" fill="none" stroke="#0054D6" strokeWidth="3" vectorEffect="non-scaling-stroke" strokeLinecap="round" />
             </svg>
          </div>
          <div className="relative z-10 text-center mt-2">
            <h3 className="text-[32px] font-extrabold text-brand-600 leading-none mb-1">+8%</h3>
            <p className="text-[9px] font-extrabold text-brand-600 uppercase tracking-widest">Projected Growth</p>
          </div>
        </div>
      </div>

    </div>
  );
}